/**
 * Study Log Dialog — displays the audit/session log for a study.
 *
 * Uses getStudyLog to fetch log records with level, message, time, detail.
 */
import { useState, useEffect, startTransition } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, CircularProgress, Typography,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { api } from '../../api/rpc';
import type { Study, StudyLogEntry } from '../../api/types';
import { gwtStyles } from '../../config/theme';
import { pushError } from '../../stores/notificationStore';

interface StudyLogDialogProps {
  open: boolean;
  study: Study | null;
  onClose: () => void;
}

const columns: GridColDef<StudyLogEntry>[] = [
  {
    field: 'time',
    headerName: 'Time',
    width: 170,
    valueFormatter: (value: number) => value ? new Date(value).toLocaleString() : '',
  },
  {
    field: 'level',
    headerName: 'Level',
    width: 80,
    cellClassName: (params) => {
      const level = params.value as string;
      if (level === 'SEVERE' || level === 'ERROR') return 'log-error';
      if (level === 'WARNING') return 'log-warn';
      return '';
    },
  },
  { field: 'message', headerName: 'Message', flex: 1, minWidth: 200 },
  { field: 'detail', headerName: 'Detail', flex: 1, minWidth: 200 },
];

export const StudyLogDialog = ({ open, study, onClose }: StudyLogDialogProps) => {
  const [entries, setEntries] = useState<StudyLogEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !study) return;
    startTransition(() => setLoading(true));
    api.getStudyLog(study.objKey)
      .then((result) => setEntries(result.records))
      .catch((e) => pushError('Failed to load study log: ' + (e instanceof Error ? e.message : String(e))))
      .finally(() => setLoading(false));
  }, [open, study]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '6px',
          border: `1px solid ${gwtStyles.borderSubtle}`,
          backgroundColor: gwtStyles.popupBg,
          height: '70vh',
        },
      }}
    >
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1 }}>
        Study Log{study ? ` — ${study.name} (${study.acc})` : ''}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0, px: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : entries.length === 0 ? (
          <Typography sx={{ py: 3, textAlign: 'center', fontSize: 13, color: 'text.secondary' }}>
            No log entries found for this study
          </Typography>
        ) : (
          <DataGrid
            rows={entries.map((e, i) => ({ ...e, id: i }))}
            columns={columns}
            density="compact"
            disableRowSelectionOnClick
            initialState={{
              sorting: { sortModel: [{ field: 'time', sort: 'desc' }] },
            }}
            sx={{
              flex: 1,
              fontSize: 12,
              '& .log-error': { color: '#f44336' },
              '& .log-warn': { color: '#ff9800' },
              '& .MuiDataGrid-cell': { py: 0.25 },
            }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontSize: 12 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
